/**
 * The list table every index screen uses.
 *
 * It owns the four states a remote list actually has — loading, failed, empty,
 * populated — because a table that only handles the fourth leaves each screen to
 * reinvent the other three, and they get it inconsistently.
 *
 * Pagination is cursor-based (`docs/API.md` §1.4), so the footer offers Previous
 * and Next and **no page numbers**. There is no way to jump to page 7 without an
 * offset, and offsets are what the API refuses to provide: rows inserted while a
 * user reads make them skip or repeat records.
 */
import { useMemo, useState } from 'react';
import { SkeletonTable, EmptyState, ErrorState } from './index.jsx';

/* ---- client-side sorting -------------------------------------------------------
   A column that has no API `sortKey` still sorts, over the rows already loaded: the
   value is `col.sortValue(row)` if given, else `row[col.key]` (objects read `.name`).
   A column with nothing sortable in it (actions, a checkbox, derived JSX only) keeps
   a plain header. Set `sortable: false` on a column to opt out explicitly. */
const plain = (v) => {
  if (v == null) return null;
  if (typeof v === 'object') return plain(v.name ?? v.full_name ?? v.label ?? null);
  return v;
};
const valueOf = (col, row) => plain(col.sortValue ? col.sortValue(row) : row[col.key]);
const isLocalSortable = (col, rows) => Boolean(col.header) && col.sortable !== false && !String(col.key).startsWith('__')
  && col.key !== 'actions' && rows.some((r) => { const v = valueOf(col, r); return v != null && v !== ''; });
const compare = (a, b) => {
  if (a == null || a === '') return b == null || b === '' ? 0 : 1; // empties last, either direction
  if (b == null || b === '') return -1;
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });
};

/**
 * @param {object} props
 * @param {{key:string, header:string, render?:(row:object)=>React.ReactNode,
 *          width?:string, align?:'start'|'center'|'end', className?:string,
 *          sortKey?:string}[]} props.columns `sortKey` is the API field this
 *          column orders by (`docs/API.md` `sort`); omit it on columns the API
 *          can't sort on — a derived/joined display value, say.
 * @param {object[]} props.rows
 * @param {boolean} [props.isLoading]
 * @param {object} [props.error]
 * @param {() => void} [props.onRetry]
 * @param {(row:object)=>void} [props.onRowClick] makes rows keyboard-activatable too
 * @param {(row:object)=>string} [props.rowKey]
 * @param {object} [props.page] the `page` envelope from the API
 * @param {() => void} [props.onNext]
 * @param {() => void} [props.onPrevious]
 * @param {boolean} [props.canGoPrevious]
 * @param {number} [props.pageSize] current rows-per-page, if the caller lets it change
 * @param {(size:number)=>void} [props.onPageSizeChange] omit to hide the page-size picker entirely
 * @param {number[]} [props.pageSizeOptions]
 * @param {string} [props.sort] current `sort` value, e.g. `full_name` or `-full_name`
 * @param {(sort:string)=>void} [props.onSort] omit to render columns as plain, unsortable headers
 * @param {string[]} [props.selected] ids of selected rows; enables the checkbox column
 * @param {(ids:string[])=>void} [props.onSelectionChange]
 * @param {React.ReactNode} [props.emptyAction] rendered inside the empty state
 */
export function DataTable({
  columns,
  rows = [],
  isLoading,
  error,
  onRetry,
  onRowClick,
  rowKey = (row) => row.id,
  page,
  onNext,
  onPrevious,
  canGoPrevious,
  pageSize,
  onPageSizeChange,
  pageSizeOptions = [10, 20, 50, 100],
  sort,
  onSort,
  selected,
  onSelectionChange,
  emptyTitle = 'Nothing here yet',
  emptyMessage,
  emptyAction,
  dense = false,
}) {
  const [local, setLocal] = useState(null); // { key, desc }
  const sortedRows = useMemo(() => {
    const col = local && columns.find((c) => c.key === local.key);
    if (!col) return rows;
    const dir = local.desc ? -1 : 1;
    return [...rows].sort((x, y) => {
      const a = valueOf(col, x); const b = valueOf(col, y);
      const emptyA = a == null || a === ''; const emptyB = b == null || b === '';
      if (emptyA || emptyB) return compare(a, b); // empties stay last regardless of direction
      return dir * compare(a, b);
    });
  }, [rows, columns, local]);
  const selectable = Boolean(onSelectionChange);
  const allSelected = selectable && rows.length > 0 && rows.every((r) => selected?.includes(rowKey(r)));

  /** Select or clear every row on the current page — never rows on other pages. */
  const toggleAll = () => {
    if (allSelected) {
      onSelectionChange(selected.filter((id) => !rows.some((r) => rowKey(r) === id)));
    } else {
      onSelectionChange([...new Set([...(selected ?? []), ...rows.map(rowKey)])]);
    }
  };

  const toggleOne = (id) => {
    onSelectionChange(selected?.includes(id) ? selected.filter((s) => s !== id) : [...(selected ?? []), id]);
  };

  if (error) return <ErrorState error={error} onRetry={onRetry} />;
  /* The columns are already known, so the wait can be the table itself: same
     `<th>`s, same widths, same row height. A spinner here collapsed the table to
     a single centred line and the whole page jumped when the rows landed —
     including any control the user was reaching for below it. */
  if (isLoading) return <SkeletonTable columns={columns} />;

  if (rows.length === 0) {
    return (
      <EmptyState icon="bi-inbox" title={emptyTitle} message={emptyMessage}>
        {emptyAction}
      </EmptyState>
    );
  }

  return (
    <>
      <div className="table-x-wrap">
        <table className={`table-x ${dense ? 'is-dense' : ''}`}>
          <thead>
            <tr>
              {selectable && (
                <th style={{ width: 36 }}>
                  <input
                    type="checkbox"
                    className="form-check-input"
                    checked={allSelected}
                    onChange={toggleAll}
                    aria-label="Select all rows on this page"
                  />
                </th>
              )}
              {columns.map((col) => {
                const serverSortable = onSort && col.sortKey;
                const localSortable = !serverSortable && isLocalSortable(col, rows);
                const active = serverSortable
                  ? !local && (sort === col.sortKey || sort === `-${col.sortKey}`)
                  : localSortable && local?.key === col.key;
                const desc = serverSortable ? active && sort === `-${col.sortKey}` : active && local.desc;
                const click = serverSortable
                  ? () => { setLocal(null); onSort(active && !desc ? `-${col.sortKey}` : col.sortKey); }
                  : () => setLocal(active && !desc ? { key: col.key, desc: true } : active && desc ? null : { key: col.key, desc: false });
                return (
                  <th
                    key={col.key}
                    style={{ width: col.width }}
                    className={`text-${col.align ?? 'start'} ${col.className ?? ''}`}
                    aria-sort={active ? (desc ? 'descending' : 'ascending') : undefined}
                  >
                    {serverSortable || localSortable ? (
                      <button
                        type="button"
                        className="btn btn-ghost p-0 border-0"
                        style={{ font: 'inherit', color: 'inherit' }}
                        onClick={click}
                      >
                        {col.header}
                        {active && (
                          <i
                            className={`bi ${desc ? 'bi-arrow-down-short' : 'bi-arrow-up-short'}`}
                            aria-hidden="true"
                          />
                        )}
                      </button>
                    ) : col.header}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {sortedRows.map((row) => {
              const id = rowKey(row);
              return (
                <tr
                  key={id}
                  className={onRowClick ? 'is-clickable' : ''}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                  // Rows that act as links must be reachable without a mouse.
                  tabIndex={onRowClick ? 0 : undefined}
                  onKeyDown={
                    onRowClick
                      ? (e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            onRowClick(row);
                          }
                        }
                      : undefined
                  }
                >
                  {selectable && (
                    <td onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        className="form-check-input"
                        checked={selected?.includes(id) ?? false}
                        onChange={() => toggleOne(id)}
                        aria-label={`Select ${id}`}
                      />
                    </td>
                  )}
                  {columns.map((col) => (
                    <td key={col.key} className={`text-${col.align ?? 'start'} ${col.className ?? ''}`}>
                      {col.render ? col.render(row) : row[col.key] ?? '—'}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {page && (
        <CursorPager
          page={page}
          onNext={onNext}
          onPrevious={onPrevious}
          canGoPrevious={canGoPrevious}
          shown={rows.length}
          pageSize={pageSize}
          onPageSizeChange={onPageSizeChange}
          pageSizeOptions={pageSizeOptions}
        />
      )}
    </>
  );
}

/**
 * The pagination footer.
 *
 * Reports "Showing 50 of 312" rather than "Page 2 of 7", because with a cursor
 * there is no total page count to state honestly — and above 10,000 rows even
 * `total` is an estimate, which the API flags and this renders as "about".
 *
 * The page-size picker is opt-in (`onPageSizeChange` must be passed) rather
 * than always-on, so every other screen using this table keeps its own fixed
 * page size until it deliberately asks for the picker too.
 */
function CursorPager({
  page, onNext, onPrevious, canGoPrevious, shown, pageSize, onPageSizeChange, pageSizeOptions,
}) {
  return (
    <div className="pager-x">
      <span className="t-xs text-muted">
        Showing {shown}
        {page.total != null && (
          <> of {page.estimated ? 'about ' : ''}{page.total.toLocaleString('en-US')}</>
        )}
      </span>
      <div className="d-flex align-items-center gap-2">
        {onPageSizeChange && (
          <label className="t-xs text-muted d-flex align-items-center gap-1">
            Rows per page
            <select
              className="form-select form-select-sm w-auto"
              value={pageSize}
              onChange={(e) => onPageSizeChange(Number(e.target.value))}
            >
              {pageSizeOptions.map((size) => <option key={size} value={size}>{size}</option>)}
            </select>
          </label>
        )}
        <button
          type="button"
          className="btn-x btn-ghost btn-sm-x"
          onClick={onPrevious}
          disabled={!canGoPrevious}
        >
          <i className="bi bi-chevron-left" aria-hidden="true" /> Previous
        </button>
        <button
          type="button"
          className="btn-x btn-ghost btn-sm-x"
          onClick={() => onNext?.(page.next_cursor)}
          disabled={!page.has_more}
        >
          Next <i className="bi bi-chevron-right" aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}
